import type { EGender, IEmployeeDTO } from '@sharedInterfaces/IEmployee';
import type { IEmployeeTalkState, INameGenderData } from '@sharedInterfaces/IEmployeeTalkState';
import { normalizeString } from './utils';

export const useEmployeeTalkChangeDetection = (
    _talkState: IEmployeeTalkState | null,
    getComponentData: <T>(id: string) => T | undefined,
) => {
    const hasPersonalDataChanges = (
        componentId: string,
        currentEmployee: IEmployeeDTO,
        localData?: unknown,
    ): boolean => {
        const componentData = (localData as INameGenderData) || getComponentData<INameGenderData>(componentId);
        if (!componentData) return false;

        const firstNameChanged =
            normalizeString(componentData.firstName) !== normalizeString(currentEmployee.firstName);
        const lastNameChanged = normalizeString(componentData.lastName) !== normalizeString(currentEmployee.lastName);
        const genderChanged = componentData.gender !== currentEmployee.gender;
        const phoneNumberChanged =
            normalizeString(componentData.phoneNumber) !== normalizeString(currentEmployee.phoneNumber);

        return firstNameChanged || lastNameChanged || genderChanged || phoneNumberChanged;
    };

    const getPersonalDataChanges = (
        componentId: string,
        currentEmployee: IEmployeeDTO,
        localData?: unknown,
    ): {
        firstName?: string;
        lastName?: string;
        gender?: EGender;
        phoneNumber?: string;
    } | null => {
        const componentData = (localData as INameGenderData) || getComponentData<INameGenderData>(componentId);
        if (!componentData || !hasPersonalDataChanges(componentId, currentEmployee, localData)) return null;

        const changes: {
            firstName?: string;
            lastName?: string;
            gender?: EGender;
            phoneNumber?: string;
        } = {};

        if (normalizeString(componentData.firstName) !== normalizeString(currentEmployee.firstName))
            changes.firstName = componentData.firstName;
        if (normalizeString(componentData.lastName) !== normalizeString(currentEmployee.lastName))
            changes.lastName = componentData.lastName;
        if (componentData.gender !== currentEmployee.gender) changes.gender = componentData.gender as EGender;
        if (normalizeString(componentData.phoneNumber) !== normalizeString(currentEmployee.phoneNumber))
            changes.phoneNumber = componentData.phoneNumber;

        return Object.keys(changes).length > 0 ? changes : null;
    };

    const hasDescriptionChanges = (
        componentId: string,
        currentEmployee: IEmployeeDTO,
        localData?: unknown,
    ): boolean => {
        const componentData = (localData as INameGenderData) || getComponentData<INameGenderData>(componentId);
        const componentDesc = normalizeString(componentData?.description);
        const employeeDesc = normalizeString(currentEmployee.description);
        return componentDesc !== employeeDesc;
    };

    return { hasPersonalDataChanges, getPersonalDataChanges, hasDescriptionChanges };
};
